import { Controller } from "@hotwired/stimulus";

// Chip-input control: user types text, presses Enter / Tab / blur to add a
// chip, click × to remove. Per-token hidden inputs carry the array values
// as `name[]` so Rails strong-params receives Array<String>.
//
// Literal commas in input text are preserved as-is (e.g. "Bonaire, Sint
// Eustatius and Saba") — only Enter / Tab / blur tokenize.
//
// Markup:
//   <div class="filter-chip-input" data-controller="filter-chip-input"
//        data-filter-chip-input-name-value="city">
//     <span class="filter-chip-input__token" data-value="Boston">
//       Boston <button data-action="filter-chip-input#remove">×</button>
//     </span>
//     <input type="text" data-filter-chip-input-target="input"
//            data-action="keydown->filter-chip-input#onKey">
//     <!-- per-token hidden inputs are appended dynamically:
//          <input type="hidden" name="city[]" value="Boston"
//                 data-chip-token="city"> -->
//   </div>
export default class extends Controller {
  static targets = ["input"];
  static values = { name: String };

  connect() {
    // Build initial hidden inputs from any server-rendered tokens
    this._serialize();
    // Tokenize on blur so partially-typed values aren't lost when focus moves
    this._onBlur = () => this._addCurrent();
    this.inputTarget.addEventListener("blur", this._onBlur);
    // BUG #11 — restore on Reset / preset-load / reloadDraft. filter_diff
    // dispatches "filter-diff:values-applied" with the FULL flat hash; rebuild
    // this control's chips + hidden inputs from detail.values[name]
    // (Array/scalar ⇒ those chips; absent ⇒ clear).
    //
    // The event is dispatched on the DRAWER ROOT (#leads-filter-drawer, which
    // carries data-controller="filter-diff"). This chip-input renders INSIDE
    // .filter-drawer__body — i.e. a DESCENDANT of that root — so a bubbling
    // event never reaches a listener on this.element (bubbling goes UP only).
    // Bind on an ancestor that actually receives it: the offcanvas / filter-diff
    // root if resolvable, else document (the event always bubbles up to it).
    this._valuesAppliedHandler = (event) =>
      this._applyValues(event.detail && event.detail.values);
    this._vaRoot =
      this.element.closest(".offcanvas") ||
      this.element.closest('[data-controller~="filter-diff"]') ||
      document;
    this._vaRoot.addEventListener("filter-diff:values-applied", this._valuesAppliedHandler);
  }

  disconnect() {
    if (this._onBlur) {
      this.inputTarget.removeEventListener("blur", this._onBlur);
    }
    if (this._valuesAppliedHandler && this._vaRoot) {
      this._vaRoot.removeEventListener("filter-diff:values-applied", this._valuesAppliedHandler);
    }
  }

  // BUG #11 — rebuild tokens + per-token hidden inputs from the full
  // filter-diff:values-applied payload. detail.values[name]:
  //   • Array  → one chip per element.
  //   • scalar → a single chip.
  //   • undefined / null (key absent) → CLEAR all chips.
  _applyValues(values) {
    const fieldName = this.nameValue;
    if (!fieldName) return;
    const raw = values ? values[fieldName] : undefined;
    const tokens = (Array.isArray(raw) ? raw : raw == null ? [] : [raw])
      .map((v) => (v == null ? "" : String(v).trim()))
      .filter(Boolean);

    // Remove every existing token span; _serialize() will rebuild hidden inputs.
    this.element
      .querySelectorAll(".filter-chip-input__token")
      .forEach((el) => el.remove());

    // Recreate chips (deduped, order-preserving) before the text input.
    const seen = new Set();
    tokens.forEach((val) => {
      if (seen.has(val)) return;
      seen.add(val);
      const span = document.createElement("span");
      span.className = "filter-chip-input__token";
      span.dataset.value = val;
      span.appendChild(document.createTextNode(`${val} `));
      const removeBtn = document.createElement("button");
      removeBtn.dataset.action = "filter-chip-input#remove";
      removeBtn.setAttribute("aria-label", `Remove ${val}`);
      removeBtn.textContent = "×";
      span.appendChild(removeBtn);
      this.element.insertBefore(span, this.inputTarget);
    });

    this._serialize();
  }

  onKey(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this._addCurrent();
    } else if (event.key === "Tab") {
      // Allow default Tab focus movement, but tokenize first
      if (this.inputTarget.value.trim() !== "") {
        this._addCurrent();
      }
    } else if (event.key === "Backspace" && !this.inputTarget.value) {
      const tokens = this.element.querySelectorAll(".filter-chip-input__token");
      const last = tokens[tokens.length - 1];
      if (last) {
        last.remove();
        this._serialize();
      }
    }
  }

  remove(event) {
    event.preventDefault();
    event.currentTarget.closest(".filter-chip-input__token")?.remove();
    this._serialize();
  }

  _addCurrent() {
    const val = (this.inputTarget.value || "").trim();
    if (!val) return;
    // dedupe
    const existing = Array.from(this.element.querySelectorAll(".filter-chip-input__token"))
      .map((t) => t.dataset.value);
    if (existing.includes(val)) {
      this.inputTarget.value = "";
      return;
    }
    const token = document.createElement("span");
    token.className = "filter-chip-input__token";
    token.dataset.value = val;
    // Safe DOM construction — project convention blocks innerHTML writes.
    token.appendChild(document.createTextNode(`${val} `));
    const removeBtn = document.createElement("button");
    removeBtn.dataset.action = "filter-chip-input#remove";
    removeBtn.setAttribute("aria-label", `Remove ${val}`);
    removeBtn.textContent = "×";
    token.appendChild(removeBtn);
    this.element.insertBefore(token, this.inputTarget);
    this.inputTarget.value = "";
    this._serialize();
  }

  // Emit per-token hidden inputs as <input name="key[]" value="…">.
  // Matches the strong-params Array<String> contract from T3.
  _serialize() {
    const fieldName = this.nameValue;
    if (!fieldName) return;

    // Remove existing per-token hidden inputs for this field
    this.element
      .querySelectorAll(`input[type="hidden"][data-chip-token="${fieldName}"]`)
      .forEach((el) => el.remove());

    const values = Array.from(this.element.querySelectorAll(".filter-chip-input__token"))
      .map((t) => t.dataset.value)
      .filter(Boolean);

    // Append one hidden input per token
    values.forEach((val) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `${fieldName}[]`;
      input.value = val;
      input.dataset.chipToken = fieldName;
      this.element.appendChild(input);
    });

    // Notify diff controller (and any other listeners) of the change.
    // Dispatch on the wrapper element so a single, stable event source exists
    // regardless of how many hidden inputs were appended/removed.
    this.element.dispatchEvent(new Event("input", { bubbles: true }));
    this.element.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
